/*
  @layer  core
  @name   Bulles de pagination pour les carrousels de Dawn
  @deps   `slider-component` de `global.js` — méthode `setSlidePosition`, propriétés `totalPages`,
          `currentPage`, `sliderItemOffset`, événement `slideChanged` ;
          classes `.slider-counter`, `.slider-counter--dots`, `.slider-counter__link--dots`,
          `.dot` et `.visually-hidden`, toutes déjà fournies par Dawn
  @i18n   aucune chaîne ici : les libellés arrivent en attributs `data-` depuis la section

  Remplace le compteur « 1 / 3 » par des bulles, sur les seuls carrousels qui portent
  `data-enet-slider-dots`. Opt-in et non global : le même compteur sert aussi les vignettes de la
  galerie produit, où des bulles n'auraient pas de sens.

  POURQUOI EN JS, ALORS QUE LE RESTE DU THÈME ÉVITE LE JS.

  Le nombre de bulles n'est pas connu au rendu. `slider-component` mesure la largeur réelle du
  conteneur pour en déduire `slidesPerPage`, puis pose `totalPages = items - slidesPerPage + 1`.
  La valeur dépend donc de l'écran et change au redimensionnement. Aucune boucle Liquid ne peut la
  produire — c'est la raison, et la seule.

  CE QU'UNE BULLE REPRÉSENTE, ET CE N'EST PAS UNE PAGE.

  Le carrousel de Dawn défile d'une carte à la fois : ses « pages » sont des positions de
  défilement, pas des groupes. Neuf produits vus trois par trois donnent sept positions, donc sept
  bulles — et non trois. Sur mobile, où une carte occupe la vue, une bulle vaut un produit : c'est
  le cas où la lecture est la plus naturelle, et c'est la surface qui a motivé la demande.

  Le nombre de bulles varie donc avec la largeur de l'écran. C'est le comportement du carrousel qui
  est ainsi, pas un défaut de ce fichier : rendre trois bulles pour neuf produits demanderait de
  faire défiler par pages entières, soit un autre carrousel.

  LE COMPTEUR N'EST PAS SUPPRIMÉ, IL PASSE EN `visually-hidden`.

  Deux raisons. C'est `slider-component` qui l'écrit — il cherche `.slider-counter--current` et
  `--total` à chaque défilement —, donc le retirer casserait sa mise à jour. Et il reste le seul
  état lisible en texte : les bulles sont des cibles de clic, le compteur dit où l'on en est.

  COUPLAGE ASSUMÉ AUX INTERNES DE DAWN, ET CE QU'IL FAUT VÉRIFIER AU CHERRY-PICK.

  Ce fichier lit `totalPages`, `currentPage` et `sliderItemOffset`, appelle `setSlidePosition` et
  écoute `slideChanged`. Rien de tout cela n'est une API publique. Chaque accès est donc gardé : si
  une propriété disparaît, les bulles ne se rendent pas et le compteur reste visible — le carrousel
  n'est jamais cassé, il retombe sur le rendu de l'amont.
*/

(() => {
  const SELECTOR = 'slider-component[data-enet-slider-dots]';
  const MAX_FRAMES = 12;

  const init = (host) => {
    if (host.dataset.enetDotsReady === 'true') return;

    const counter = host.querySelector('.slider-counter');
    if (!counter) return;

    host.dataset.enetDotsReady = 'true';

    const label = host.dataset.enetDotsLabel || '';
    const separator = host.dataset.enetDotsOf || '';
    const controlled = host.querySelector('[id^="Slider-"]');

    const list = document.createElement('div');
    list.className = 'slider-counter slider-counter--dots';
    list.hidden = true;
    counter.classList.add('visually-hidden');
    counter.after(list);

    const setActive = () => {
      const current = Number.isInteger(host.currentPage) ? host.currentPage : 1;
      Array.from(list.children).forEach((button, index) => {
        const isActive = index === current - 1;
        button.classList.toggle('slider-counter__link--active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    const render = () => {
      const total = host.totalPages;

      // Une seule position : rien à paginer, et une bulle unique serait un contrôle inerte.
      if (!Number.isInteger(total) || total < 2) {
        list.hidden = true;
        list.replaceChildren();
        return false;
      }

      list.hidden = false;

      if (list.children.length !== total) {
        const dots = [];

        for (let index = 1; index <= total; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'slider-counter__link slider-counter__link--dots link';
          button.setAttribute('aria-label', `${label} ${index} ${separator} ${total}`.replace(/\s+/g, ' ').trim());
          if (controlled) button.setAttribute('aria-controls', controlled.id);

          button.addEventListener('click', () => {
            if (typeof host.setSlidePosition !== 'function' || !host.sliderItemOffset) return;
            host.setSlidePosition((index - 1) * host.sliderItemOffset);
          });

          const dot = document.createElement('span');
          dot.className = 'dot';
          button.appendChild(dot);
          dots.push(button);
        }

        list.replaceChildren(...dots);
      }

      setActive();
      return true;
    };

    host.addEventListener('slideChanged', setActive);

    /*
      `initPages` de Dawn tourne dans son `connectedCallback`, dont l'ordre par rapport à ce
      fichier n'est pas garanti : `totalPages` peut ne pas être encore posé au premier essai. On
      réessaie sur quelques trames, puis on laisse tomber — un `setInterval` ouvert coûterait plus
      que le rendu qu'il attend.
    */
    let frames = 0;
    const attempt = () => {
      if (render() || frames >= MAX_FRAMES) return;
      frames += 1;
      requestAnimationFrame(attempt);
    };
    attempt();

    // Le nombre de positions dépend de la largeur : il change avec l'écran et avec la police.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => render()).observe(host);
    }
  };

  const start = () => document.querySelectorAll(SELECTOR).forEach(init);

  customElements.whenDefined('slider-component').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  });

  // Rechargement d'une section dans l'éditeur de thème : le balisage est neuf, la garde aussi.
  document.addEventListener('shopify:section:load', start);
})();
